import { Alert, Box, Button, Collapse, Link, Paper, Typography } from '@mui/material';
import { LoginCallback as OktaLoginCallback } from '@okta/okta-react';
import { ProgressOverlay } from '@signanthealth/modular-ui';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '../config';

/**
 * Login callback component for Okta authentication
 * Displays a loading spinner while the authentication process completes
 */
export const LoginCallback: React.FC = () => {
  return <ProgressOverlay inProgress={true} fullOverlay message="Completing authentication..." />;
};

/**
 * Error component that displays a user-friendly error message
 * with an expandable section to show technical details
 */
interface AuthErrorProps {
  error: Error;
}

const AuthError: React.FC<AuthErrorProps> = ({ error }) => {
  const [showDetails, setShowDetails] = useState(false);
  const navigate = useNavigate();

  const handleReturnToLogin = () => {
    // Clear any auth state and navigate to the application root
    navigate(config.applicationPath || '/', { replace: true });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
        padding: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: '100%',
          padding: 4,
        }}
      >
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            An error occurred during authentication. Please try logging in again.
          </Typography>
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReturnToLogin}
            fullWidth
          >
            Return to Login
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => setShowDetails(!showDetails)}
            sx={{ cursor: 'pointer' }}
          >
            {showDetails ? 'Hide' : 'Show'} error details
          </Link>
        </Box>

        <Collapse in={showDetails}>
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: 'grey.50',
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <Typography variant="caption" component="div" sx={{ mb: 1, fontWeight: 'bold' }}>
              Error Details:
            </Typography>
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
              }}
            >
              {error.message || 'Unknown error'}
              {error.stack && `\n\n${error.stack}`}
            </Typography>
          </Paper>
        </Collapse>
      </Paper>
    </Box>
  );
};

/**
 * Wrapper that uses Okta's LoginCallback with custom loading UI and error handling
 */
export const LoginCallbackRoute: React.FC = () => {
  return (
    <OktaLoginCallback
      loadingElement={<LoginCallback />}
      errorComponent={AuthError}
    />
  );
};
